import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  message,
  Tag,
  Tooltip,
  DatePicker,
  Badge,
  Alert,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  DollarOutlined,
  LineChartOutlined,
  TeamOutlined,
  CalculatorOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import PermissionGuard from '../components/PermissionGuard';

const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

const PerformanceManagement = () => {
  console.log('PerformanceManagement组件被渲染');
  const [activeTab, setActiveTab] = useState('commission-rules');
  const [loading, setLoading] = useState(false);
  
  // 提成规则相关状态
  const [rules, setRules] = useState([]);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm] = Form.useForm();
  const [tiers, setTiers] = useState([
    { min_amount: 0, max_amount: 50000, rate: 1 },
    { min_amount: 50000, max_amount: 100000, rate: 2 },
    { min_amount: 100000, max_amount: null, rate: 3 }
  ]);
  
  // 业绩数据相关状态
  const [performanceData, setPerformanceData] = useState([]);
  const [performanceFilters, setPerformanceFilters] = useState({});
  
  // 提成计算相关状态
  const [calculateModalVisible, setCalculateModalVisible] = useState(false);
  const [calculateForm] = Form.useForm();
  const [calculateResult, setCalculateResult] = useState(null);
  
  // 基础数据
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // 加载基础数据
  useEffect(() => {
    loadBasicData();
    if (activeTab === 'commission-rules') {
      loadCommissionRules();
    } else if (activeTab === 'performance') {
      loadPerformanceData();
    }
  }, [activeTab]);
  
  const loadBasicData = async () => {
    try {
      // 加载部门列表
      const departmentsResponse = await api.get('/departments');
      setDepartments(departmentsResponse.data.data || []);
      
      // 加载岗位列表
      const positionsResponse = await api.get('/positions');
      setPositions(positionsResponse.data.data || []);
      
      // 加载员工列表
      const employeesResponse = await api.get('/employees');
      setEmployees(employeesResponse.data.data?.employees || []);
    } catch (error) {
      console.error('加载基础数据失败:', error);
    }
  };
  
  // 加载提成规则
  const loadCommissionRules = async () => {
    setLoading(true);
    try {
      const response = await api.get('/performance/commission/rules');
      setRules(response.data.data || []);
    } catch (error) {
      console.error('加载提成规则失败:', error);
      message.error('加载提成规则失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 加载业绩数据
  const loadPerformanceData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (performanceFilters.department_id) {
        params.department_id = performanceFilters.department_id;
      }
      if (performanceFilters.employee_id) {
        params.employee_id = performanceFilters.employee_id;
      }
      if (performanceFilters.dateRange) {
        params.start_date = performanceFilters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = performanceFilters.dateRange[1].format('YYYY-MM-DD');
      }
      
      const response = await api.get('/performance/performance', { params });
      setPerformanceData(response.data.data || []);
    } catch (error) {
      console.error('加载业绩数据失败:', error);
      message.error('加载业绩数据失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理提成规则提交
  const handleRuleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const submitData = {
        ...values,
        tiers
      };
      
      if (editingRule) {
        // 更新规则
        await api.put(`/performance/commission/rules/${editingRule.id}`, submitData);
        message.success('提成规则更新成功');
      } else {
        // 创建规则
        await api.post('/performance/commission/rules', submitData);
        message.success('提成规则创建成功');
      }
      
      setRuleModalVisible(false);
      loadCommissionRules();
    } catch (error) {
      console.error('提交提成规则失败:', error);
      message.error('提交提成规则失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理提成规则删除
  const handleRuleDelete = async (id) => {
    try {
      setLoading(true);
      await api.delete(`/performance/commission/rules/${id}`);
      message.success('提成规则删除成功');
      loadCommissionRules();
    } catch (error) {
      console.error('删除提成规则失败:', error);
      message.error('删除提成规则失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理提成计算
  const handleCalculateSubmit = async (values) => {
    try {
      setLoading(true);
      const response = await api.post('/performance/commission/calculate', values);
      setCalculateResult(response.data.data);
      message.success('提成计算成功');
    } catch (error) {
      console.error('计算提成失败:', error);
      message.error('计算提成失败');
    } finally {
      setLoading(false);
    }
  };
  
  // 处理梯度添加
  const handleAddTier = () => {
    setTiers([...tiers, { min_amount: 0, max_amount: null, rate: 0 }]);
  };
  
  // 处理梯度删除
  const handleRemoveTier = (index) => {
    const newTiers = [...tiers];
    newTiers.splice(index, 1);
    setTiers(newTiers);
  };
  
  // 处理梯度变更
  const handleTierChange = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setTiers(newTiers);
  };
  
  // 处理编辑规则
  const handleEditRule = (rule) => {
    setEditingRule(rule);
    ruleForm.setFieldsValue({
      name: rule.name,
      rule_type: rule.rule_type,
      target_level: rule.target_level,
      department_id: rule.department_id,
      position_id: rule.position_id,
      effective_date: rule.effective_date ? new Date(rule.effective_date) : null
    });
    setTiers(rule.tiers || []);
    setRuleModalVisible(true);
  };
  
  // 处理新建规则
  const handleCreateRule = () => {
    setEditingRule(null);
    ruleForm.resetFields();
    setTiers([
      { min_amount: 0, max_amount: 50000, rate: 1 },
      { min_amount: 50000, max_amount: 100000, rate: 2 },
      { min_amount: 100000, max_amount: null, rate: 3 }
    ]);
    setRuleModalVisible(true);
  };
  
  // 提成规则列配置
  const ruleColumns = [
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.department_name || '所有部门'} - {record.position_name || '所有岗位'}
          </div>
        </div>
      ),
    },
    {
      title: '规则类型',
      dataIndex: 'rule_type',
      key: 'rule_type',
      render: (type) => {
        const typeMap = {
          'individual': '个人',
          'team': '团队',
          'department': '部门'
        };
        return <Tag color="blue">{typeMap[type] || type}</Tag>;
      },
    },
    {
      title: '目标层级',
      dataIndex: 'target_level',
      key: 'target_level',
      render: (level) => {
        const levelMap = {
          'employee': '员工',
          'team_leader': '团队主管',
          'manager': '部门经理',
          'director': '总监'
        };
        return <Tag color="green">{levelMap[level] || level}</Tag>;
      },
    },
    {
      title: '生效日期',
      dataIndex: 'effective_date',
      key: 'effective_date',
      render: (date) => date ? new Date(date).toLocaleDateString() : '立即生效',
    },
    {
      title: '梯度数量',
      dataIndex: 'tiers',
      key: 'tier_count',
      render: (tiers) => <Badge count={tiers?.length || 0} showZero color="orange" />,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <PermissionGuard permission="commission.manage">
            <Tooltip title="编辑">
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEditRule(record)} />
            </Tooltip>
          </PermissionGuard>
          <PermissionGuard permission="commission.manage">
            <Tooltip title="删除">
              <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleRuleDelete(record.id)} />
            </Tooltip>
          </PermissionGuard>
        </Space>
      ),
    },
  ];
  
  // 业绩数据列配置
  const performanceColumns = [
    {
      title: '员工',
      dataIndex: 'employee_name',
      key: 'employee_name',
    },
    {
      title: '部门',
      dataIndex: 'department_name',
      key: 'department_name',
    },
    {
      title: '业绩金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `¥${parseFloat(amount).toLocaleString()}`,
    },
    {
      title: '业绩日期',
      dataIndex: 'performance_date',
      key: 'performance_date',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: '记录类型',
      dataIndex: 'record_type',
      key: 'record_type',
      render: (type) => {
        const typeMap = {
          'sales': '销售',
          'project': '项目',
          'service': '服务'
        };
        return <Tag color="purple">{typeMap[type] || type}</Tag>;
      },
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
  ];
  
  return (
    <div style={{ padding: '10px 16px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
            <DollarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            业绩管理
          </h2>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            管理提成规则、查看业绩数据和计算提成
          </p>
        </div>

        {/* 统计卡片 */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="提成规则数量"
                value={rules.length}
                prefix={<CalculatorOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="业绩记录数量"
                value={performanceData.length}
                prefix={<LineChartOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="部门数量"
                value={departments.length}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Statistic
                title="员工数量"
                value={employees.length}
                prefix={<HistoryOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="提成规则" key="commission-rules">
            <Card
              size="small"
              style={{ marginBottom: '16px' }}
              extra={
                <PermissionGuard permission="commission.manage">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateRule}>
                    新建提成规则
                  </Button>
                </PermissionGuard>
              }
            >
              <Table
                columns={ruleColumns}
                dataSource={rules}
                rowKey="id"
                loading={loading}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 条记录`,
                }}
              />
            </Card>
          </TabPane>

          <TabPane tab="业绩数据" key="performance">
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="部门">
                    <Select
                      placeholder="选择部门"
                      style={{ width: '100%' }}
                      onChange={(value) => setPerformanceFilters({ ...performanceFilters, department_id: value })}
                    >
                      <Option value={null}>所有部门</Option>
                      {departments.map(dept => (
                        <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="员工">
                    <Select
                      placeholder="选择员工"
                      style={{ width: '100%' }}
                      onChange={(value) => setPerformanceFilters({ ...performanceFilters, employee_id: value })}
                    >
                      <Option value={null}>所有员工</Option>
                      {employees.map(emp => (
                        <Option key={emp.id} value={emp.id}>{emp.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="日期范围">
                    <RangePicker
                      style={{ width: '100%' }}
                      onChange={(dates) => setPerformanceFilters({ ...performanceFilters, dateRange: dates })}
                    />
                  </Form.Item>
                </Col>
                <Col span={4} style={{ textAlign: 'right', paddingTop: '8px' }}>
                  <Button type="primary" onClick={loadPerformanceData}>
                    查询
                  </Button>
                </Col>
              </Row>
            </Card>

            <Card>
              <Table
                columns={performanceColumns}
                dataSource={performanceData}
                rowKey="id"
                loading={loading}
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 条记录`,
                }}
              />
            </Card>
          </TabPane>

          <TabPane tab="提成计算" key="commission-calculate">
            <Card>
              <PermissionGuard permission="commission.calculate">
                <Form
                  form={calculateForm}
                  layout="vertical"
                  onFinish={handleCalculateSubmit}
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name="employee_id"
                        label="员工"
                        rules={[{ required: true, message: '请选择员工' }]}
                      >
                        <Select placeholder="选择员工">
                          {employees.map(emp => (
                            <Option key={emp.id} value={emp.id}>{emp.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="performance_amount"
                        label="业绩金额"
                        rules={[{ required: true, message: '请输入业绩金额' }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          formatter={(value) => `¥ ${value}`}
                          parser={(value) => value.replace(/¥\s?|(,*)/g, '')}
                          min={0}
                          step={1000}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="rule_id"
                        label="提成规则"
                        rules={[{ required: true, message: '请选择提成规则' }]}
                      >
                        <Select placeholder="选择提成规则">
                          {rules.map(rule => (
                            <Option key={rule.id} value={rule.id}>{rule.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={24} style={{ textAlign: 'center' }}>
                      <Button type="primary" htmlType="submit" icon={<CalculatorOutlined />} loading={loading}>
                        计算提成
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </PermissionGuard>

              {calculateResult && (
                <Card style={{ marginTop: '24px' }}>
                  <Alert
                    message="提成计算结果"
                    type="success"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="业绩金额"
                        value={calculateResult.performance_amount}
                        precision={2}
                        prefix="¥"
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="提成金额"
                        value={calculateResult.commission_amount}
                        precision={2}
                        prefix="¥"
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="提成比例"
                        value={(calculateResult.commission_amount / calculateResult.performance_amount * 100).toFixed(2)}
                        suffix="%"
                        valueStyle={{ color: '#faad14' }}
                      />
                    </Col>
                  </Row>
                  <div style={{ marginTop: '16px' }}>
                    <h4>使用规则：{calculateResult.rule_name}</h4>
                  </div>
                </Card>
              )}
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* 提成规则编辑模态框 */}
      <Modal
        title={editingRule ? '编辑提成规则' : '新建提成规则'}
        open={ruleModalVisible}
        onCancel={() => setRuleModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setRuleModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={() => ruleForm.submit()}
          >
            {editingRule ? '更新' : '创建'}
          </Button>,
        ]}
      >
        <Form form={ruleForm} layout="vertical" onFinish={handleRuleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="规则名称"
                rules={[{ required: true, message: '请输入规则名称' }]}
              >
                <Input placeholder="请输入规则名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rule_type"
                label="规则类型"
                rules={[{ required: true, message: '请选择规则类型' }]}
              >
                <Select placeholder="请选择规则类型">
                  <Option value="individual">个人</Option>
                  <Option value="team">团队</Option>
                  <Option value="department">部门</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="target_level"
                label="目标层级"
                rules={[{ required: true, message: '请选择目标层级' }]}
              >
                <Select placeholder="请选择目标层级">
                  <Option value="employee">员工</Option>
                  <Option value="team_leader">团队主管</Option>
                  <Option value="manager">部门经理</Option>
                  <Option value="director">总监</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effective_date" label="生效日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="department_id" label="适用部门">
                <Select placeholder="选择部门（不选则适用所有部门）">
                  <Option value={null}>所有部门</Option>
                  {departments.map(dept => (
                    <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="position_id" label="适用岗位">
                <Select placeholder="选择岗位（不选则适用所有岗位）">
                  <Option value={null}>所有岗位</Option>
                  {positions.map(pos => (
                    <Option key={pos.id} value={pos.id}>{pos.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: '24px' }}>
            <h4>提成梯度</h4>
            <div style={{ marginBottom: '16px' }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddTier}>
                添加梯度
              </Button>
            </div>
            {tiers.map((tier, index) => (
              <Card key={index} style={{ marginBottom: '16px' }}>
                <Row gutter={16}>
                  <Col span={7}>
                    <Form.Item label="最低金额">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(value) => `¥ ${value}`}
                        parser={(value) => value.replace(/¥\s?|(,*)/g, '')}
                        value={tier.min_amount}
                        onChange={(value) => handleTierChange(index, 'min_amount', value)}
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item label="最高金额（空表示无上限）">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(value) => value ? `¥ ${value}` : '无上限'}
                        parser={(value) => value.replace(/¥\s?|(,*)/g, '')}
                        value={tier.max_amount}
                        onChange={(value) => handleTierChange(index, 'max_amount', value)}
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={7}>
                    <Form.Item label="提成比例">
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(value) => `${value}%`}
                        parser={(value) => value.replace('%', '')}
                        value={tier.rate}
                        onChange={(value) => handleTierChange(index, 'rate', value)}
                        min={0}
                        max={100}
                        step={0.1}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={3} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Button danger icon={<DeleteOutlined />} onClick={() => handleRemoveTier(index)} />
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PerformanceManagement;